import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import {
  initializeSquare,
  createPaymentForm,
  processPayment,
  PaymentResult,
  handleSquareError,
  validatePaymentAmount,
  generateIdempotencyKey
} from '../lib/square'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'

interface PaymentMethods {
  card: any
  applePay: any
  googlePay: any
  payments: any
}

interface PaymentContextType {
  // Payment methods
  paymentMethods: PaymentMethods | null
  
  // Payment state
  isProcessing: boolean
  paymentError: string | null
  
  // Methods
  initializePayment: (containerId: string, amount: number) => Promise<void>
  processCardPayment: (amount: number, orderId: string) => Promise<PaymentResult>
  processApplePayment: (amount: number, orderId: string) => Promise<PaymentResult>
  processGooglePayment: (amount: number, orderId: string) => Promise<PaymentResult>
  clearError: () => void
  
  // Utilities
  isApplePayAvailable: boolean
  isGooglePayAvailable: boolean
}

const PaymentContext = createContext<PaymentContextType | null>(null)

export const usePayment = () => {
  const context = useContext(PaymentContext)
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider')
  }
  return context
}

export const PaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethods | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false)
  const [isGooglePayAvailable, setIsGooglePayAvailable] = useState(false)
  const cardRef = useRef<any>(null)

  // Initialize Square SDK on component mount
  useEffect(() => {
    initializeSquare().catch(error => {
      console.error('Failed to initialize Square SDK:', error)
      setPaymentError('Payment system unavailable. Please try again later.')
    })

    return () => {
      if (cardRef.current) {
        cardRef.current.destroy().catch((error: any) => {
          console.error('Error destroying card on unmount:', error)
        })
      }
    }
  }, [])

  const initializePayment = useCallback(async (containerId: string, amount: number) => {
    try {
      setPaymentError(null)

      if (!validatePaymentAmount(amount)) {
        throw new Error('Invalid payment amount')
      }

      // Destroy existing card instance before creating new one
      if (cardRef.current) {
        await cardRef.current.destroy()
        cardRef.current = null
      }

      const methods = await createPaymentForm(containerId, amount)
      cardRef.current = methods.card
      setPaymentMethods(methods)

      // Check availability of digital wallets
      // Apple Pay works in Safari, Google Pay works in Chrome/Chromium browsers
      const isChromium = !!(window as any).chrome || navigator.userAgent.includes('Chrome')
      setIsApplePayAvailable(!!methods.applePay)
      setIsGooglePayAvailable(!!methods.googlePay && isChromium)

    } catch (error) {
      console.error('Payment initialization failed:', error)
      setPaymentError('Failed to initialize payment form. Please refresh and try again.')
    }
  }, [])

  // Call backend payment processing endpoint
  const processPaymentBackend = async (
    sourceId: string,
    amount: number,
    orderId: string,
    verificationToken?: string
  ) => {
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Get current session for auth token — server derives userId from JWT
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No active session')
    }

    const response = await fetch('/.netlify/functions/process-payment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        sourceId,
        amount: amount / 100, // Convert cents to dollars
        orderId,
        verificationToken,
      }),
    })

    const data = await response.json()

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Payment processing failed')
    }

    return data
  }

  const processCardPayment = async (amount: number, orderId: string): Promise<PaymentResult> => {
    if (!paymentMethods?.card) {
      throw new Error('Card payment method not initialized')
    }

    setIsProcessing(true)
    setPaymentError(null)

    try {
      // Tokenize payment method
      const tokenResult = await processPayment(paymentMethods.card, amount, orderId)

      if (tokenResult.errors) {
        const errorMessage = handleSquareError(tokenResult.errors)
        setPaymentError(errorMessage)
        return tokenResult
      }

      // Process payment via backend
      if (tokenResult.token) {
        const backendResult = await processPaymentBackend(
          tokenResult.token,
          amount,
          orderId,
          tokenResult.verificationToken
        )

        return {
          token: backendResult.paymentId,
          payment: backendResult
        }
      }

      throw new Error('Failed to tokenize payment')
    } catch (error) {
      const errorMessage = error.message || 'Payment failed. Please try again.'
      setPaymentError(errorMessage)
      return { errors: [{ category: 'PAYMENT_ERROR', code: 'PROCESSING_ERROR', detail: errorMessage }] }
    } finally {
      setIsProcessing(false)
    }
  }

  const processApplePayment = async (amount: number, orderId: string): Promise<PaymentResult> => {
    if (!paymentMethods?.applePay) {
      throw new Error('Apple Pay not available')
    }

    setIsProcessing(true)
    setPaymentError(null)

    try {
      // Tokenize payment method
      const tokenResult = await processPayment(paymentMethods.applePay, amount, orderId)

      if (tokenResult.errors) {
        const errorMessage = handleSquareError(tokenResult.errors)
        setPaymentError(errorMessage)
        return tokenResult
      }

      // Process payment via backend
      if (tokenResult.token) {
        const backendResult = await processPaymentBackend(
          tokenResult.token,
          amount,
          orderId,
          tokenResult.verificationToken
        )

        return {
          token: backendResult.paymentId,
          payment: backendResult
        }
      }

      throw new Error('Failed to tokenize payment')
    } catch (error) {
      const errorMessage = error.message || 'Apple Pay failed. Please try again.'
      setPaymentError(errorMessage)
      return { errors: [{ category: 'PAYMENT_ERROR', code: 'PROCESSING_ERROR', detail: errorMessage }] }
    } finally {
      setIsProcessing(false)
    }
  }

  const processGooglePayment = async (amount: number, orderId: string): Promise<PaymentResult> => {
    if (!paymentMethods?.googlePay) {
      throw new Error('Google Pay not available')
    }

    setIsProcessing(true)
    setPaymentError(null)

    try {
      // Tokenize payment method
      const tokenResult = await processPayment(paymentMethods.googlePay, amount, orderId)

      if (tokenResult.errors) {
        const errorMessage = handleSquareError(tokenResult.errors)
        setPaymentError(errorMessage)
        return tokenResult
      }

      // Process payment via backend
      if (tokenResult.token) {
        const backendResult = await processPaymentBackend(
          tokenResult.token,
          amount,
          orderId,
          tokenResult.verificationToken
        )

        return {
          token: backendResult.paymentId,
          payment: backendResult
        }
      }

      throw new Error('Failed to tokenize payment')
    } catch (error) {
      const errorMessage = error.message || 'Google Pay failed. Please try again.'
      setPaymentError(errorMessage)
      return { errors: [{ category: 'PAYMENT_ERROR', code: 'PROCESSING_ERROR', detail: errorMessage }] }
    } finally {
      setIsProcessing(false)
    }
  }

  const clearError = () => {
    setPaymentError(null)
  }

  return (
    <PaymentContext.Provider
      value={{
        paymentMethods,
        isProcessing,
        paymentError,
        initializePayment,
        processCardPayment,
        processApplePayment,
        processGooglePayment,
        clearError,
        isApplePayAvailable,
        isGooglePayAvailable
      }}
    >
      {children}
    </PaymentContext.Provider>
  )
}

export default PaymentProvider